import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { deleteCookie } from 'cookies-next';
import { Block, BLOCKS, Inline, INLINES } from '@contentful/rich-text-types';

import { ICard, IOptionSelectFields } from '../../../types/contentful';

import { useSubscriptionsContext } from '../../context/Subscriptions';
import { trackWithFingerprint } from '../../utils/trackWithFingerprint';
import { getCookieDomain } from '../../utils/cookies';

import RichText from '../RichText';
import { EmbeddedInlineEntryNode } from '../RichText/Nodes';

import GlLink from '../Link';

export function OptionSelect({ options, disclaimer }: IOptionSelectFields) {
  const router = useRouter();
  const stateMap = options.reduce(
    (acc, { fields }) => ({ ...acc, [fields.id]: false }),
    {}
  );
  const [routeLoading, setRouteLoading] =
    useState<Record<string, boolean>>(stateMap);

  const { setPayment } = useSubscriptionsContext();

  useEffect(() => {
    deleteCookie('gl_purchase_subscription_external_id', {
      domain: getCookieDomain()
    });
  }, []);

  return (
    <div
      className="block-container mb-12 mt-12 desktop:mb-16 desktop:mt-3"
      data-testid="subscription-options"
    >
      <div className="layout flex flex-col flex-wrap tablet:flex-row tablet:gap-2 desktop:py-0">
        {options.map(({ fields }: ICard) => {
          const {
            id,
            title,
            details,
            ctaUrl = '/purchase-subscriptions/customize'
          } = fields ?? {};
          return (
            <div
              key={title}
              className="h-fit w-full flex-shrink-0 flex-grow-0 p-2 tablet:w-[calc(50%-26px)] desktop:w-[calc(25%-29px)] desktop:px-3 desktop:py-4"
            >
              <div className="flex h-[236px] w-full flex-col rounded-2xl border border-gray bg-white p-6 desktop:h-[264px]">
                <p className="mb-2 text-[28px] font-medium leading-[32px] tablet:text-[26px]">
                  {title}
                </p>

                <div className="flex-1">
                  <RichText
                    data={details}
                    overrideOptions={{
                      [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
                        <p className="text-left text-[16px] leading-5 text-text desktop:text-[18px] desktop:leading-[26px]">
                          {children}
                        </p>
                      )
                    }}
                  />
                </div>

                <button
                  onClick={() => {
                    setRouteLoading({ ...stateMap, [id]: true });
                    const amount = parseInt(id, 10);
                    trackWithFingerprint('CTA Clicked', {
                      page_url: window.location.href,
                      module: 'gifting_option',
                      amount
                    });
                    setPayment?.(amount);
                    router.push(`${ctaUrl}?selectedPlan=${id}`);
                  }}
                  data-testid={`option-select-button-${id}`}
                  className="flex h-16 min-w-[146px] cursor-pointer items-center justify-center rounded-xl border-0 bg-yellow px-3 text-[18px] font-medium leading-4 text-black outline-0 transition-colors duration-200 ease-in-out hover:bg-green focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-limegreen active:bg-green desktop:min-w-[168px] desktop:rounded-2xl"
                >
                  {!!routeLoading[id as string] ? 'Loading...' : 'Select'}
                </button>
              </div>
            </div>
          );
        })}

        <div className="box-column w-full flex-shrink-0 flex-grow-0 px-2 py-0">
          <RichText
            data={disclaimer}
            overrideOptions={{
              [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
                <p className="text-center text-[14px] leading-[22px] text-text desktop:text-[18px] desktop:leading-[26px]">
                  {children}
                </p>
              ),
              [INLINES.HYPERLINK]: (node, children) => (
                <GlLink
                  href={node.data.uri}
                  className="font-normal text-text underline transition-colors duration-200 ease-in-out hover:text-navy desktop:hover:text-green"
                >
                  {children}
                </GlLink>
              ),
              [INLINES.EMBEDDED_ENTRY]: (node, children) => (
                <EmbeddedInlineEntryNode
                  node={node}
                  colour="secondaryText"
                  fontWeight="p"
                >
                  {children}
                </EmbeddedInlineEntryNode>
              )
            }}
          />
        </div>
      </div>
    </div>
  );
}
